import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import styles from './SchoolList.module.css';
import Button from 'components/Button/Button';
import { api } from 'api/axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import SearchInput from 'components/SearchInput/SearchInput';
import Loading from 'components/Loading/Loading';

interface School {
    institutional_id: string;
    name: string;
    school_id?: string; // If there's a custom ID
    unique_code?: string;
}

const SchoolList: React.FC = () => {
    const [schools, setSchools] = useState<School[]>([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const navigate = useNavigate();

    useEffect(() => {
        const fetchSchools = async () => {
            try {
                setIsLoading(true);
                const response = await api.get('/schools');
                setSchools(response.data.data || response.data);
                setError(null);
            } catch (err: any) {
                console.error('Error fetching schools:', err);
                setError(err.response?.data?.message || 'Failed to fetch school list');
            } finally {
                setIsLoading(false);
            }
        };

        fetchSchools();
    }, []);

    const handleAddSchool = () => {
        navigate('/school-management/add');
    };

    const handleViewDetails = (id: string) => {
        navigate(`/school-management/view/${id}`);
    };

    const handleEditDetails = (id: string) => {
        navigate(`/school-management/edit/${id}`);
    };

    const filteredSchools = schools.filter(school =>
        school.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (school.school_id && school.school_id.toLowerCase().includes(searchQuery.toLowerCase())) ||
        String(school.institutional_id).toLowerCase().includes(searchQuery.toLowerCase())
    );

    return (
        <div className={styles.pageContainer}>
            <div className={styles.header}>
                <div className={styles.heading}>
                    <h1 className={styles.title}>School Management</h1>
                    <p className={styles.subtitle}>Manage and monitor registered educational institutions</p>
                </div>
                <Button variant="primary" onClick={handleAddSchool}>
                    + Add New School
                </Button>
            </div>

            <SearchInput
                value={searchQuery}
                onChange={setSearchQuery}
                placeholder="Search schools by name or ID..."
            />

            <div className={styles.listContainer}>
                {isLoading ? (
                    <Loading message="Loading schools..." />
                ) : error ? (
                    <div className={styles.errorContainer}>{error}</div>
                ) : filteredSchools.length === 0 ? (
                    <div className={styles.emptyContainer}>
                        <p>{searchQuery ? 'No schools match your search.' : 'No schools found. Add your first school to get started.'}</p>
                        {!searchQuery && (
                            <Button variant="secondary" onClick={handleAddSchool}>
                                Create School
                            </Button>
                        )}
                    </div>
                ) : (
                    <>
                        <div className={styles.tableHeader}>
                            <div>School Name</div>
                            <div>School ID</div>
                            <div style={{ textAlign: 'right' }}>Actions</div>
                        </div>
                        {filteredSchools.map((school) => (
                            <div key={school.institutional_id} className={styles.schoolItem}>
                                <div className={styles.schoolName} title={school.name}>{school.name}</div>
                                <div className={styles.schoolId}>{school.school_id || school.institutional_id}</div>
                                <div className={styles.actions}>
                                    <Button
                                        variant="secondary"
                                        size="sm"
                                        onClick={() => handleViewDetails(school.institutional_id)}
                                    >
                                        View Details
                                    </Button>
                                    <Button
                                        variant="secondary"
                                        size="sm"
                                        onClick={() => handleEditDetails(school.institutional_id)}
                                    >
                                        Edit Details
                                    </Button>
                                </div>
                            </div>
                        ))}
                    </>
                )}
            </div>
        </div>
    );
};

export default SchoolList;
